import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Input,
	OnInit,
	Output
} from '@angular/core';

import { HlsUtilService } from 'vbrick-player-src/HlsUtil.Service';
import { IVbSource } from 'vbrick-player-src/IVbSource';
import { NativePlaybackUtilService } from 'vbrick-player-src/NativePlaybackUtil.Service';
import { SUBTITLE_OPTION_CAPTIONS } from 'vbrick-player-src/VbSubtitleMenuButton.Component';
import { TextTrackHelperService } from 'vbrick-player-src/TextTrackHelper.Service';
import { TextTrackKinds } from 'vbrick-player-src/TextTrackKinds';
import { TextTrackModes } from 'vbrick-player-src/TextTrackModes';
import { userAgentUtil } from 'vbrick-player-src/UserAgentUtil';
import { VbPlaybackManagerComponent } from 'vbrick-player-src/VbPlaybackManager.Component';
import { VideogularComponent } from 'vbrick-player-src/videogular/Videogular.Component';

import { VgHlsSupportService } from './VgHlsSupport.Service';

import { IHlsPlayerLevelsUpdatedData } from './IHlsPlayerLevelsUpdatedData';
import { IOnHlsLevelsChangedData } from './IOnHlsLevelsChangedData';
import { HLS_PLAYER_LEVELS_UPDATED } from './HlsPlayer';
import { buildMainPlayerLevelsChangedData } from './HlsLevelsUtils';
import { IHlsJsTextTrack } from './IHlsJsExtended';

import {
	VG_HLS_PLAYER_CROSS_ORIGIN_ERROR,
	VgHlsPlayer
} from './VgHlsPlayer';

/**
 * @ngdoc directive
 * @name com.vbrick.videogular.plugins.hls.directive:vgHls
 * @restrict A
 * @description
 * Adds HLS support for vg-media.
 * This plugin requires hls.js file available at hls.js project:
 * https://github.com/video-dev/hls.js
 *
 * <pre>
 * <videogular vg-theme="config.theme.url" vg-autoplay="config.autoPlay">
 *	<vg-media vg-src="sources" vg-hls></vg-media>
 * </videogular>
 * </pre>
 *
 */
@Component({
	selector: 'vg-hls',
	changeDetection: ChangeDetectionStrategy.OnPush,
	template: ``,
    standalone: false
})
export class VgHlsComponent implements OnInit {
	@Input() public closedCaptionsTranslation: string;
	@Input() public playbackManager: VbPlaybackManagerComponent;

	@Output() public onHlsLevelsChanged: EventEmitter<IOnHlsLevelsChangedData> = new EventEmitter();

	private isMulticast: boolean;
	private player: VgHlsPlayer;

	constructor(
		private HlsUtilService: HlsUtilService,
		private NativePlaybackUtil: NativePlaybackUtilService,
		private TextTrackHelper: TextTrackHelperService,
		private VG_HLS_SUPPORT: VgHlsSupportService,
		private vgApi: VideogularComponent
	) {}

	public ngOnInit(): void {
		const IS_NATIVE_SUPPORT_BLACKLISTED: boolean = !this.NativePlaybackUtil.canPlayNatively({ src: location.protocol, type: 'application/x-mpegURL' } as IVbSource);
		const IS_SUPPORTED_ENV: boolean = (!this.VG_HLS_SUPPORT.isNative || IS_NATIVE_SUPPORT_BLACKLISTED) && this.VG_HLS_SUPPORT.isMSE;

		if (IS_SUPPORTED_ENV) {
			this.vgApi.registerPlaybackPlugin(source => this.loadHlsPlayer(source));
		}
	}

	private loadHlsPlayer({ src, type, isLive, revConnectInfo }: IVbSource): false | (() => void) {
		const isSupportedSrc: boolean = src && this.HlsUtilService.isHLS(src, type) && !this.HlsUtilService.isMixedContent(src);
		const isStartPosition: boolean = src && !isLive && !!this.vgApi.startTime;

		this.isMulticast = this.HlsUtilService.isMulticast(src, type);

		if (isSupportedSrc && !this.vgApi.forceFlashHlsPlayback) {
			if (this.isMulticast) {
				this.HlsUtilService.isMulticastAgentRunning(src)
					.catch(() => {
						console.error('Vbrick Multicast Agent is not reachable.');
						this.vgApi.onVideoError();
					});
			}

			this.player = new VgHlsPlayer(src, this.vgApi.mediaElement, this.vgApi, isLive, {
				captionsTextTrack1Label: this.closedCaptionsTranslation,
				maxBufferHole: 5, //seems to help firefox avoid getting stuck in a frag loading loop
				startPosition: isStartPosition ? this.vgApi.startTime : -1
			}, this.isMulticast, revConnectInfo);

			this.player.on(HLS_PLAYER_LEVELS_UPDATED, (data: IHlsPlayerLevelsUpdatedData) => this.onEmitLevels(data));

			//error handling for Flash fallback behavior
			this.player.on(VG_HLS_PLAYER_CROSS_ORIGIN_ERROR, () => this.playbackManager.hlsCrossOriginError());

			//expose analytics data on API
			Object.defineProperties(this.vgApi, {
				bandwidth: {
					configurable: true,
					get: () => this.player ?
						this.player.bwEstimate :
						null
				},

				bitrate: {
					configurable: true,
					get: () => this.player ?
						this.player.bitrate :
						null
				}
			});

			//expose closed caption display toggle
			this.vgApi.setCaptionsEnabled = (isEnabled: boolean) => this.setCaptionsEnabled(isEnabled);

			//if captions have been enabled already, get it applied correctly
			if (this.vgApi.activeSubtitle === SUBTITLE_OPTION_CAPTIONS) {
				this.setCaptionsEnabled(true);
			}

			// ignore decoding errors to allow for the plugin to instead recover
			this.vgApi.isIgnoreDecodingErrors = isLive; // TODO: extend to VOD

			return () => this.unloadHlsPlayer();
		}

		return false;
	}

	private onEmitLevels(data: IHlsPlayerLevelsUpdatedData): void {
		const payload: IOnHlsLevelsChangedData = buildMainPlayerLevelsChangedData(data);

		this.onHlsLevelsChanged.emit(payload);
	}

	private setCaptionsEnabled(isEnabled: boolean): void {
		//hls.js creates (or reuses) its own closed captions track, but this happens only when caption data is read.
		//In order to get the toggling working prior to the data being read, need to manually populate the expected text track.
		const textTrack: IHlsJsTextTrack = this.TextTrackHelper.getOrCreateTrack(this.vgApi.mediaElement, TextTrackKinds.CAPTIONS, this.closedCaptionsTranslation, false);

		if (!textTrack) {
			return;
		}

		textTrack.textTrack1 = true; // this is how hls.js identifies its caption track
		textTrack.mode = isEnabled ? TextTrackModes.SHOWING : TextTrackModes.HIDDEN;
		textTrack.oncuechange = userAgentUtil.isFirefox && isEnabled ? this.modifyCueSettings : undefined;
	}

	private modifyCueSettings(event): void {
		const textTrack: IHlsJsTextTrack = event.target || {};
		const previousCuesLen: number = textTrack.previousCuesLength;

		if (!textTrack.cues?.length || previousCuesLen === textTrack.cues.length) {
			return;
		}
		//workaround - firefox setting position of some cues close to 100
		//because of that it is showing CC in a single vertical line.
		const index = previousCuesLen === undefined ? 0 : previousCuesLen;
		for(let i: number = index; i < textTrack.cues.length; i++) {
			const cue = textTrack.cues[i] as VTTCue;
			if (cue?.align === 'left' && (cue.position as number) > 75) {
				cue.position = 75;
			}
		}
		event.target.previousCuesLength = textTrack.cues.length;
	}

	private unloadHlsPlayer(): void {
		//clean up existing hls.js player instance (if any)
		if (this.player) {
			this.vgApi.stop();

			//Dettach hls.js from the mediaElement
			try {
				this.player.destroy();
			} catch(ex) {
				// deliberately empty
			}

			this.onHlsLevelsChanged.emit(null);

			this.player = null;
			this.vgApi.isIgnoreDecodingErrors = false;
		}

		//clean up custom accessors on the API
		Object.defineProperties(this.vgApi, {
			bandwidth: { value: null },
			bitrate: { value: null }
		});

		this.vgApi.setCaptionsEnabled = null;
	}
}
